/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-26
 */

// Add third-party dependencies.
import { BottomNavigationAction } from '@mui/material';

// Add local dependencies.
import TabBadge from './TabBadge';

/**
 * Props for the TabItem component.
 */
interface TabItemProps {
  /**
   * The tab label.
   */
  label: string;
  /**
   * The tab icon component.
   */
  icon: React.ComponentType;
  /**
   * Optional badge count for notifications.
   */
  badge?: number;
  /**
   * The tab value for Material-UI BottomNavigation.
   */
  value: number;
  /**
   * Optional click handler for navigation.
   */
  onClick?: () => void;
}

/**
 * Individual tab item component for the bottom tab bar.
 * Provides touch-optimized tab with icon, label, and optional badge.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The tab item component
 *
 * @example
 * return (
 *   <TabItem
 *     label="Dashboard"
 *     icon={DashboardIcon}
 *     badge={3}
 *     value={0}
 *   />
 * );
 */
const TabItem: React.FC<TabItemProps> = ({
  label,
  icon: IconComponent,
  badge,
  value,
  onClick,
}) => (
  <BottomNavigationAction
    label={label}
    value={value}
    onClick={onClick}
    icon={
      badge && badge > 0 ? (
        <TabBadge count={badge}>
          <IconComponent />
        </TabBadge>
      ) : (
        <IconComponent />
      )
    }
    sx={{
      // Ensure minimum touch target size
      minWidth: 44,
      minHeight: 44,
      // Improve touch feedback
      '&:active': {
        transform: 'scale(0.95)',
        transition: 'transform 0.1s ease-in-out',
      },
    }}
  />
);

export default TabItem;
